import { workspaceFailureMessage } from './workspace-feedback';

import type { CurrentWorkspace, WorkspaceOperation } from '@mio/shared';

export type WorkspaceAction = 'activate' | 'checkpoint' | 'submit' | 'stop';
/** Whether this activity can start compute at all; only `openable` may show progress before a job exists. */
export type WorkspaceAvailability = 'openable' | 'unpublished' | 'restricted' | 'loading';
/**
 * How long a single confirmed step may run before the learner is told it is slow
 * and offered a status check. A first allocation cold-starts the environment and
 * regularly needs more than a minute, so the notice must not fire before then.
 */
export const SLOW_STEP_NOTICE_MS = 120_000;
export interface WorkspaceProgressModel {
  title: string;
  description: string;
  steps: string[];
  current: number;
  running: boolean;
  failed: boolean;
  /** Not an error, but the learner may want to check the status. */
  attention?: boolean;
}

/** Only confirmed phases advance a step. Step counts are not estimates of time remaining. */
export function workspaceProgress({
  workspace,
  operation,
  action,
  busy,
  connected,
  enabled,
  launchFailed = false,
  availability = 'openable',
}: {
  workspace: CurrentWorkspace['workspace'];
  operation: WorkspaceOperation | null;
  action: WorkspaceAction | null;
  busy: boolean;
  connected: boolean;
  enabled: boolean;
  launchFailed?: boolean;
  availability?: WorkspaceAvailability;
}): WorkspaceProgressModel {
  const phase = operation?.phase;
  // Jobs initiated by another tab or automatic maintenance have no local command.
  const effectiveAction = action ?? operation?.action ?? null;
  // A backup or idle stop that maintenance started is retried by the next
  // sweep; its outcome is never a failure the learner has to act on.
  const failed =
    workspace?.state === 'failed' ||
    (operation?.status === 'failed' && operation.maintenance !== true) ||
    launchFailed;
  if (workspace?.state === 'stopping' && workspace.failureCode === 'workspace_stop_uncertain')
    return {
      title: 'Confirming your pause',
      description: workspaceFailureMessage('workspace_stop_uncertain'),
      steps: [],
      current: 0,
      running: false,
      failed: false,
      attention: true,
    };
  if (!enabled)
    return {
      title: 'Coding is unavailable',
      description:
        'You can still read the workshop instructions. The coding environment is not enabled here yet.',
      steps: [],
      current: 0,
      running: false,
      failed: false,
    };
  const idle = !busy && !operation;
  if (idle && workspace?.fieldActive)
    return {
      title: 'Workbench is using your coding environment',
      description:
        'Save Workbench and open this workshop from the notice above; Workbench resumes later where you left off.',
      steps: [],
      current: 0,
      running: false,
      failed: false,
    };
  // Nothing is running and nothing will start: say so instead of counting seconds.
  if (idle && availability !== 'openable' && (!workspace || workspace.state === 'allocated'))
    return {
      title:
        availability === 'unpublished'
          ? 'No coding environment for this workshop'
          : availability === 'restricted'
            ? 'Coding is unavailable for your enrollment'
            : 'Checking this workshop',
      description:
        availability === 'unpublished'
          ? 'This activity is a brief only. Read it here; a workspace appears once its task is published.'
          : availability === 'restricted'
            ? 'Your enrollment does not currently allow this workshop’s coding environment. Contact support if you think this is wrong.'
            : 'Loading the workshop’s task and your access…',
      steps: [],
      current: 0,
      running: false,
      failed: false,
    };
  if (idle && workspace?.state === 'stopping' && !workspace.failureCode)
    return {
      title: 'Pausing your workspace',
      description: 'Your files are being saved and the environment paused. This finishes shortly.',
      steps: [],
      current: 0,
      running: true,
      failed: false,
    };
  if (idle && (workspace?.state === 'deleting' || workspace?.state === 'deleted'))
    return {
      title: 'This workspace is being retired',
      description: 'Your saved backups are kept. Reopen the workshop to start a fresh environment.',
      steps: [],
      current: 0,
      running: false,
      failed: false,
    };
  const saving = effectiveAction && effectiveAction !== 'activate';
  let steps: string[];
  let current: number;
  let title: string;
  let description: string;
  if (saving) {
    steps = [
      'Save and back up your work',
      effectiveAction === 'submit'
        ? 'Record your submission'
        : effectiveAction === 'stop'
          ? 'Pause the environment'
          : 'Confirm the backup',
    ];
    current = phase === 'done' ? 2 : phase === 'source_saved' || phase === 'stopping' ? 1 : 0;
    title =
      effectiveAction === 'submit'
        ? 'Submitting your work'
        : effectiveAction === 'stop'
          ? 'Saving and pausing'
          : 'Backing up your work';
    description =
      'Keep this tab open until the save is confirmed. Your editor will unlock when the operation finishes.';
  } else if (
    busy &&
    !effectiveAction &&
    workspace?.state === 'ready' &&
    (!operation || phase === 'pending')
  ) {
    steps = ['Confirm the workspace operation'];
    current = 0;
    title = 'Updating your workspace';
    description =
      'We are checking the save and workspace status. Your editor will unlock when this finishes.';
  } else {
    steps = ['Start the environment', 'Prepare workshop files', 'Connect the editor'];
    const prepared = ['target_prepared', 'committed', 'editor_ready'].includes(phase ?? '');
    const compute = prepared || phase === 'source_saved' || workspace?.state === 'ready';
    current =
      connected && !busy
        ? 3
        : prepared || (!busy && workspace?.state === 'ready')
          ? 2
          : compute
            ? 1
            : 0;
    title = current === 2 ? 'Connecting your editor' : 'Preparing your workspace';
    description = 'You can read the instructions while we get your coding environment ready.';
  }
  if (failed)
    return {
      title: launchFailed ? 'Editor connection needs attention' : 'Workspace needs attention',
      description: launchFailed
        ? 'Follow the connection message above to reconnect. Your workshop instructions remain available.'
        : 'The operation could not finish. Check the message above, then use Refresh IDE when available to retry.',
      steps,
      current,
      running: false,
      failed: true,
    };
  if (workspace?.state === 'stopped' && !busy)
    return {
      title: 'Your workspace is paused',
      description: 'Your files and explanation are saved. Resume when you are ready to continue.',
      steps: [],
      current: 0,
      running: false,
      failed: false,
    };
  const running = current < steps.length;
  if (!running) {
    title = saving
      ? effectiveAction === 'submit'
        ? 'Your submission is saved'
        : effectiveAction === 'stop'
          ? 'Your workspace is paused'
          : 'Your work is backed up'
      : 'Your workspace is ready';
    description = saving
      ? 'The operation finished successfully.'
      : 'Your workshop files and editor are ready.';
  }
  return { title, description, steps, current, running, failed: false };
}
